// deployment_class: customer_env_allowed
/**
 * CEO redesign — project (deal) data for the live project experience.
 * Riverdale demo deals. Drives BOTH the visual project grid (the photo-card
 * overview) AND the full project view (cover + grab-scrollable Data Ticker).
 *
 * `tint` is the hue for Kathedral's OWN cover visual (a branded gradient +
 * architectural skyline) — not a stock photo, not a copy. `img` is reserved
 * and swap-ready: drop a real photo URL there later and the cover uses it.
 */
export interface Deal {
  key: string;
  name: string;   // the project name, as the agency names it
  type: string;   // "LIHTC 9%"
  dev: string;    // the development partner
  phaseIdx: number; // 0..8 (current phase)
  progress: number; // 0..100 (for the donut)
  budget: string;
  dscr: string;
  status: string;
  rag: ['g' | 'y' | 'r', string]; // status pill: [tone, short label]
  units: number;
  tint: number;     // hue for the owned cover visual (swap-ready for a real photo)
  img?: string;     // reserved — real photo URL overrides the branded cover
  su: Array<[string, number, string]>; // [source, pct, value]
  partner: string;
  next: string[];
}

export const PHASES = ['Planning', 'Early Design', 'Final Design', 'ROW Acq', 'Bid', 'Pre-Con', 'Construction', 'Close-out', 'Stabilized'];

/**
 * What is IN each lifecycle milestone (bug_UI_1h — "TELL me what is in the
 * specific Milestone. EVERYTHING"). Grounded in the standard PHA/LIHTC
 * development lifecycle: each phase's purpose, the key activities it contains,
 * the gate that must clear to advance, and the accountable owner. The drill
 * fills its space with this so a clicked milestone reads as a real work packet.
 */
export interface PhaseDetail {
  summary: string;
  activities: string[];
  gate: string;
  owner: string;
}
export const PHASE_DETAIL: Record<string, PhaseDetail> = {
  Planning: { summary: 'Establish feasibility and control before capital is committed.', activities: ['Site control / option secured', 'Market study + needs analysis', 'Preliminary pro-forma + sources plan', 'Community + stakeholder engagement'], gate: 'Go / No-Go feasibility approval', owner: 'Development' },
  'Early Design': { summary: 'Translate the program into a schematic the numbers can price.', activities: ['Schematic design + unit mix', 'Preliminary budget (hard + soft)', 'Zoning / entitlement path confirmed', 'Design-review concept sign-off'], gate: 'Concept + preliminary budget approval', owner: 'Architect · Development' },
  'Final Design': { summary: 'Lock the drawings, permits, and a firm cost basis.', activities: ['Construction documents (100% CD)', 'Building-permit set submitted', 'Guaranteed maximum price (GMP)', 'Value-engineering reconciliation'], gate: 'Permit issued + GMP accepted', owner: 'Architect · General Contractor' },
  'ROW Acq': { summary: 'Take clean, unencumbered title to the site.', activities: ['Acquisition closing', 'Title commitment + survey cleared', 'Phase I environmental (Phase II if flagged)', 'Relocation plan (if occupied)'], gate: 'Clear title + clean environmental', owner: 'Acquisitions · Legal' },
  Bid: { summary: 'Competitively price and award the work.', activities: ['GC / trade solicitation', 'Subcontractor bid leveling', 'Bid vs. GMP reconciliation', 'Contract award + bonds'], gate: 'Bid award within budget', owner: 'Procurement · Development' },
  'Pre-Con': { summary: 'Close the capital stack and mobilize.', activities: ['Financial close (debt + LIHTC equity)', 'Final permits + NTP issued', 'Insurance + builder’s risk bound', 'Mobilization + preconstruction meeting'], gate: 'Financial close + Notice to Proceed', owner: 'Finance · Development' },
  Construction: { summary: 'Build it — on schedule, on budget, on draw.', activities: ['Vertical construction', 'Monthly requisitions / draws', 'Lender + investor inspections', 'Change-order + contingency tracking'], gate: 'Substantial completion / TCO', owner: 'General Contractor · Owner’s Rep' },
  'Close-out': { summary: 'Certify costs and convert to permanent.', activities: ['Punch list + Certificate of Occupancy', 'Cost certification + IRS 8609 (LIHTC)', 'Final draw + retainage release', 'Permanent-loan conversion prep'], gate: 'Cost certification accepted', owner: 'Finance · Development' },
  Stabilized: { summary: 'Reach stabilized operations and hand to asset management.', activities: ['Lease-up to stabilized occupancy', 'Stabilized DSCR achieved', 'Permanent conversion complete', 'Compliance / reporting cadence live'], gate: 'Stabilization + conversion', owner: 'Asset Management' },
};
/**
 * `DEALS` / `DEAL_ORDER` LEFT THIS FILE on 2026-09-01 (E3 lock: "There's no more
 * demo data associated with our production").
 *
 * They were ten Riverdale deals compiled into the customer bundle and rendered
 * RAW — unlike the module catalog, nothing stripped them — so the project grid,
 * the analyzer, the milestone drills and the breadcrumb showed Willow, Phoenix
 * and Cedar to every agency on every island.
 *
 * Deals are CONTENT and now arrive the way module content does: through
 * `DashboardContent`, read with `useDeals()`. There is deliberately no empty
 * constant left behind to import — a fallback here is how the demo data got in.
 * The values are preserved verbatim as a test fixture at
 * `test/support/riverdale-demo-content.ts`.
 *
 * What stays: the `Deal` SHAPE and `PHASES` — Kathedral's own development
 * model, identical for every customer.
 */
